import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

export default function IntegrationsSettings() {
  const { can } = useApp();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: "", label: "" });

  const { data, isLoading } = useQuery({ queryKey: ["settings-integrations"], queryFn: () => get("/settings/integrations") });

  const add = useMutation({
    mutationFn: (body) => post("/settings/integrations/receiver-directory", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings-integrations"] }); toast.success("Added"); setAdding(false); setForm({ email: "", label: "" }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not add"),
  });
  const remove = useMutation({
    mutationFn: (email) => del(`/settings/integrations/receiver-directory/${encodeURIComponent(email)}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings-integrations"] }); toast.success("Removed"); },
  });

  if (!can("settings", "override")) {
    return <EmptyState icon={Lock} title="Super Admin only" description="You don't have access to this page." />;
  }

  const directory = data?.receiver_email_directory || [];

  return (
    <div>
      <PageHeader title="Integrations" subtitle="Google mailbox/calendar integration settings.">
        <Button data-testid="add-receiver-btn" onClick={() => setAdding((s) => !s)}><Plus className="mr-1.5 h-4 w-4" />Add Sender</Button>
      </PageHeader>

      {isLoading ? <Skeleton className="h-64" /> : (
        <Section title="Receiver Email Directory"
          action={<span className="text-xs text-muted-foreground">Senders allowed into personal (non-company-email) mailboxes</span>}>
          {adding && (
            <div className="border-b border-border/60 bg-muted/30 p-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Sender Email *</Label><Input data-testid="receiver-email" className="mt-1" placeholder="client@example.com" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
                <div><Label>Label (optional)</Label><Input data-testid="receiver-label" className="mt-1" placeholder="e.g. Acme Corp" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} /></div>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
                <Button size="sm" data-testid="receiver-submit" disabled={!form.email || add.isPending}
                  onClick={() => add.mutate(form)}>Add</Button>
              </div>
            </div>
          )}
          {!directory.length ? (
            <EmptyState icon={Users} title="No senders allowlisted yet" description="Add senders here so personal-email mailboxes can ingest their mail." />
          ) : (
            <div className="divide-y divide-border">
              {directory.map((d) => (
                <div key={d.email} data-testid={`receiver-row-${d.email}`} className="flex items-center justify-between px-4 py-3 text-sm">
                  <div><div className="font-medium">{d.label}</div><div className="text-xs text-muted-foreground">{d.email}</div></div>
                  <ConfirmDelete testid={`del-receiver-${d.email}`} title={`Remove ${d.email}?`}
                    trigger={<button className="text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                    onConfirm={() => remove.mutate(d.email)} />
                </div>
              ))}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
